import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Sidebar from "./Sidebar";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import TopNavBar from "./TopNavBar";
import MobileHeader from "./MobileHeader";

import MobileNavSheet from "./MobileNavSheet";
import { useDashboardSessionTimeout } from "../hooks/useDashboardSessionTimeout";
import { useStyledTheme } from "../hooks/useStyledTheme";
import { releaseFocusFromBackground } from "../utils/modalFocus";
import AnnouncementHost from "./AnnouncementHost";
import { AskNoticeboardProvider } from "./AskNoticeboard/AskNoticeboardProvider";
import AskNoticeboardButton from "./AskNoticeboard/AskNoticeboardButton";
import { CERTIFICATES } from "../routes";
import { isCoursePath } from "../domain/help/course";

const LayoutWrapper = styled.div<{ isAuthPage: boolean }>`
  padding: ${({ isAuthPage, theme }) =>
    isAuthPage ? "0" : `${theme.spacing[6]} ${theme.spacing[4]}`};
  padding-top: ${({ isAuthPage, theme }) =>
    isAuthPage ? "0" : theme.dimensions.topNavHeight + 40}px;
  margin-top: ${({ isAuthPage }) => (isAuthPage ? "0" : "56px")};
  min-height: ${({ isAuthPage }) => (isAuthPage ? "0" : "80vh")};

  @media (max-width: 768px) {
    padding: ${({ isAuthPage, theme }) =>
      isAuthPage ? "0" : theme.spacing[4]};
  }
`;

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthPage = ["/login", "/signup"].includes(location.pathname);
  const [sidebarMinimized, setSidebarMinimized] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const sidebarWidth = sidebarMinimized ? 60 : 220;
  const theme = useTheme();
  const styledTheme = useStyledTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const hideTopHeaderOnRoute =
    /^\/notice\/[^/]+\/?$/.test(location.pathname) ||
    /^\/circles\/[^/]+$/.test(location.pathname) ||
    /^\/user-management\/[^/]+$/.test(location.pathname) ||
    location.pathname.startsWith(CERTIFICATES) ||
    location.pathname.startsWith("/create-notice/birthdays") ||
    location.pathname.startsWith("/create-notice/celebrations") ||
    location.pathname.startsWith("/create-notice/images/") ||
    /^\/create-notice\/signatures\/?$/.test(location.pathname) ||
    /^\/create-notice\/templates\/new\/?$/.test(location.pathname) ||
    isCoursePath(location.pathname);

  // Auto-logout after 2 hours idle (see `DASHBOARD_IDLE_TIMEOUT_MINUTES` in useDashboardSessionTimeout).
  useDashboardSessionTimeout();

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    // Ask Noticeboard wraps every page, so ⌘K / Ctrl+K works even where the top bar is hidden
    <AskNoticeboardProvider>
      {!isAuthPage ? (
        <Box sx={{ display: "flex", minHeight: "100vh" }}>
          {isMobile && (
            <AppBar
              position="fixed"
              color="primary"
              sx={{ zIndex: theme.zIndex.drawer + 1, boxShadow: 1 }}
            >
              <Toolbar
                variant="regular"
                sx={{
                  minHeight: 56,
                  height: 56,
                  px: 2,
                }}
              >
                <IconButton
                  edge="start"
                  color="inherit"
                  aria-label="open drawer"
                  onClick={() => {
                    releaseFocusFromBackground();
                    setMobileNavOpen(true);
                  }}
                  sx={{ mr: 1 }}
                  data-testid="mobile-menu-button"
                >
                  <MenuIcon />
                </IconButton>
                <Box
                  component="img"
                  src="/logo192.png"
                  alt="Noticeboard"
                  onClick={() => navigate("/")}
                  sx={{
                    height: 30,
                    width: "auto",
                    cursor: "pointer",
                    ml: "auto",
                    objectFit: "contain",
                    borderRadius: styledTheme.borderRadius.layout,
                  }}
                />
                <Box sx={{ ml: 1, display: "flex" }}>
                  <AskNoticeboardButton onAppBar />
                </Box>
              </Toolbar>
            </AppBar>
          )}
          {!isMobile && (
            <Sidebar
              minimized={sidebarMinimized}
              onToggleMinimized={setSidebarMinimized}
              mobileOpen={mobileSidebarOpen}
              onMobileClose={() => setMobileSidebarOpen(false)}
            />
          )}
          <Box
            sx={{
              flex: 1,
              px: { xs: 2, md: 3 },
              pt: { xs: 0, md: 1 },
              pb: { xs: 2, md: 3 },
              width: isMobile ? "100vw" : `calc(100vw - ${sidebarWidth}px)`,
              maxWidth: "100vw",
              mx: "auto",
              transition: "width 0.2s",
              overflowX: "hidden",
            }}
          >
            {/* Spacer for mobile AppBar */}
            {isMobile && <Box sx={theme.mixins.toolbar} />}
            {/* Show MobileHeader on mobile, TopNavBar on desktop */}
            {isMobile
              ? !(
                  hideTopHeaderOnRoute ||
                  location.pathname === "/" ||
                  location.pathname === "/dashboard" ||
                  location.pathname === "/create-notice" ||
                  location.pathname === "/help/videos" ||
                  location.pathname === "/library" ||
                  location.pathname.startsWith("/create/worksheets") ||
                  location.pathname === "/resources" ||
                  location.pathname.startsWith("/resources/")
                ) && <MobileHeader />
              : !hideTopHeaderOnRoute && <TopNavBar />}
            {children}
            <AnnouncementHost />
          </Box>
        </Box>
      ) : (
        <LayoutWrapper isAuthPage={isAuthPage}>{children}</LayoutWrapper>
      )}

      {!isAuthPage && isMobile && (
        <MobileNavSheet
          open={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
        />
      )}
    </AskNoticeboardProvider>
  );
};

export default Layout;
